// S36: a row's script: the columns, the row's name, its cells, how a change is drawn, and a card row's
// facts line, figure and what its figure's tooltip says, Why it ranks or the breakdown
// (ActivityTable::LAYOUT_CARD, P5, D121), and the advice its open row heads with.

// Where an advice's first sentence ends: a full stop, question or exclamation mark, then a space and a
// capital or an opening quote. A figure's decimal point ("position 8.4") has no space after it.
const SENTENCE_END = /([.!?])\s+(?=[A-Z“"(])/;

// What joins a card row's facts into its one line (P21, plan 4.3): "position 14.5 · 2,856 impressions".
const FACTS_JOIN = " · ";

// What a click on a card row leaves alone: its controls, which do their own thing.
const CARD_CONTROLS = "a, button, input, label, select, textarea, summary";

export default function rowPart() {
    return {
        // ------------------------------------------------------------------------------ the columns

        nameColumn() {
            return this.table.columns[0];
        },

        // Every column after the first, which is the row header.
        otherColumns() {
            return this.table.columns.slice(1);
        },

        columnOf(key) {
            return this.table.columns.find((column) => column.key === key) || null;
        },

        positionColumn() {
            return this.table.columns.find((column) => column.kind === "position") || null;
        },

        hasFilter(name) {
            return this.table.filters.includes(name);
        },

        hasBulk() {
            return this.table.bulkActions.length > 0;
        },

        hasRowActions() {
            return this.rows.some((row) => row.actions.length > 0);
        },

        isCard() {
            return this.table.layout === "card";
        },

        // A card row with buttons but no open row to hold them (Sort my searches' Other queries, S42.1):
        // its buttons stay on the card, in a cell at its right edge. Decided row by row, so a row that
        // opens never shows its buttons twice.
        isClosedCard(row) {
            return this.isCard() && row.actions.length > 0 && !this.hasDetail(row);
        },

        hasClosedCardActions() {
            return this.rows.some((row) => this.isClosedCard(row));
        },

        // A closed card's buttons, Move to topic first, as the v3 spec's inbox row has them; the rest keep
        // the table's order.
        closedActionsOf(row) {
            const actions = this.actionsOf(row);

            return actions.includes("move") ? ["move", ...actions.filter((action) => action !== "move")] : actions;
        },

        // Whether the card row at `index` of the page draws the thin line above it that parts it from the row
        // before (P21: one white card, its rows parted by lines). The page's first row draws none, nor does
        // a row under a group's heading, which draws its own (groups.js).
        hasRuleAbove(index) {
            return index > 0 && this.groupHeadAt(index) === null;
        },

        // A card row's stage chip, only where stages tell rows apart: the row has a stage, or the table
        // filters by one, so its Not sorted rows say so beside the rest.
        showsStageChip(row) {
            return (row.stage !== null && row.stage !== undefined) || this.table.filters.includes("stage");
        },

        // Every cell a row shows, plus the checkbox and actions columns, for the notice and the open row
        // that span them. A card row's buttons sit beside its figure (P5, D98), so it has an actions column
        // only when its rows open into nothing and keep theirs in a cell of their own.
        columnCount() {
            return this.table.columns.length + (this.hasBulk() ? 1 : 0) + (this.hasRowActions() && (!this.isCard() || this.hasClosedCardActions()) ? 1 : 0);
        },

        // ------------------------------------------------------------------------------- one row

        rowById(id) {
            return this.rows.find((row) => row.id === id) || null;
        },

        cellOf(row, column) {
            return row.cells[column.key] || { value: null, display: null };
        },

        // The masked word stands in for an unsafe search's text everywhere it would be said (D47).
        isMasked(row) {
            return row.isUnsafe === true && this.revealed[row.id] !== true;
        },

        toggleReveal(id) {
            if (this.revealed[id]) {
                delete this.revealed[id];
            } else {
                this.revealed[id] = true;
            }
        },

        nameOf(row) {
            if (this.isMasked(row)) {
                return this.strings.unsafe;
            }

            return this.cellOf(row, this.nameColumn()).display ?? this.strings.noData;
        },

        selectRowLabel(row) {
            return this.strings.selectRow.replace(":row", this.nameOf(row));
        },

        changeDirection(cell) {
            if (cell.value === null || cell.value === undefined || cell.value === 0) {
                return "flat";
            }

            return cell.value > 0 ? "up" : "down";
        },

        changeArrow(cell) {
            return { up: "▲", down: "▼", flat: "–" }[this.changeDirection(cell)];
        },

        changeToneClass(cell) {
            return { positive: "text-green-700", negative: "text-red-700" }[cell.tone] || "text-gray-700";
        },

        // A figure's hint as one line, for today's row's title: each {label, display} the activity sent.
        hintText(cell) {
            return Array.isArray(cell.hint) ? cell.hint.map((line) => `${line.label} ${line.display}`).join(" · ") : null;
        },

        // A cell as words alone, for the cells a card row does not show: a screen reader still reads each
        // figure under its header, as it does on today's row.
        cellWords(row, column) {
            const cell = this.cellOf(row, column);

            if (cell.display === null || cell.display === undefined) {
                return this.strings.noData;
            }

            if (column.kind === "chips") {
                return cell.display.length > 0 ? cell.display.join(", ") : this.strings.noData;
            }

            if (column.kind === "change") {
                return `${this.strings.change[this.changeDirection(cell)]} ${cell.display}`;
            }

            return String(cell.display);
        },

        // -------------------------------------------------------------------------------- a card row

        // A press anywhere on a card but a control opens or shuts it, through its one disclosure, so
        // the pointer and the keyboard share one button and one state. A press that ends a text
        // selection is reading, not asking.
        toggleFromCard(event, row) {
            if (!this.hasDetail(row) || this.noticeOf(row.id) || event.target.closest(CARD_CONTROLS)) {
                return;
            }

            const selection = typeof window !== "undefined" && window.getSelection ? window.getSelection() : null;

            if (selection && selection.toString() !== "") {
                return;
            }

            event.currentTarget.querySelector("[data-row-expand]")?.click();
        },

        captionOf(column) {
            return column.caption ?? column.label;
        },

        // A card row's facts as its one line under the search. A row an answer brought with no facts (an
        // update that left the key out) draws an empty line rather than breaking the row.
        factsLine(row) {
            return Array.isArray(row.facts) ? row.facts.join(FACTS_JOIN) : "";
        },

        // From the row's place on screen, as detailId() is, so it is a valid id whatever the row's own id holds.
        breakdownId(index) {
            return `activity-table-${this.table.id}-breakdown-${index}`;
        },

        breakdownLabel(row, column) {
            return this.strings.breakdown.label
                .replace(":figure", this.cellOf(row, column).display ?? this.strings.noData)
                .replace(":caption", this.captionOf(column))
                .replace(":row", this.nameOf(row));
        },

        // Whether a row's figure opens Why it ranks: the row carries its factors (D121). Each part goes in
        // through a function, so a `$` in a search is never read as a replacement pattern.
        hasWhy(row) {
            return this.hasDetail(row) && Array.isArray(row.detail.factors) && row.detail.factors.length > 0;
        },

        figureLabel(row, column) {
            if (!this.hasWhy(row)) {
                return this.breakdownLabel(row, column);
            }

            // The words under the figure, Current best search included (why.js), so the name holds what is on screen.
            return this.strings.whyLabel
                .replace(":caption", () => this.figureCaption(row, column))
                .replace(":figure", () => this.cellOf(row, column).display ?? this.strings.noData)
                .replace(":row", () => this.nameOf(row));
        },

        figureTitle(row) {
            return this.hasWhy(row) ? this.factorsTitleOf(row) : this.breakdownTitle();
        },

        /**
         * An open row's What to do (P5, D121): the advice's first sentence as the action, and the rest as
         * its reason, from today's one sentence. An advice of one sentence is all action.
         */
        adviceOf(row) {
            const advice = String(row.detail?.advice ?? "");
            const match = SENTENCE_END.exec(advice);

            if (!match) {
                return { action: advice, reason: "" };
            }

            const end = match.index + match[1].length;

            return { action: advice.slice(0, end), reason: advice.slice(end).trim() };
        },

        // The table's own words when it names them (Win back clicks' clicks lost); the Opportunity
        // score's otherwise.
        breakdownTitle() {
            return this.table.breakdown?.title ?? this.strings.breakdown.title;
        },

        breakdownLines(row, column) {
            return this.cellOf(row, column).hint ?? [];
        },

        breakdownFooter(row) {
            if (this.table.breakdown?.footer) {
                return this.table.breakdown.footer;
            }

            return row.rawScore ? this.strings.breakdown.footer.replace(":raw", row.rawScore.display) : "";
        },

    };
}
